import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { MessageCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useConversationSummary } from '../hooks/use-conversation-summary'
import { useMessageApi } from '../hooks/use-message-api'
import { initializeI18n } from '../lib/i18n'
import { cn, mergeSx, sxToCSS, sxToStyleTag } from '../lib/utils'
import { allowMarkRead, isMarkReadSuppressed } from '../lib/mark-read-suppression'
import type { EntityChatTriggerProps } from '../types'
import EntityChatPanel from './entity-chat-panel'
import { useMessageComponents } from './message-components-provider'
import { Badge } from './ui/badge'
import { Button } from './ui/button'

// Module-level singleton: one matchMedia listener shared across all instances.
// Prevents 50+ resize listeners from flooding React with simultaneous state updates.
const LARGE_SCREEN_QUERY = '(min-width: 1025px)'
const _subscribers = new Set<(value: boolean) => void>()
let _isLargeScreen =
  typeof window !== 'undefined'
    ? window.matchMedia(LARGE_SCREEN_QUERY).matches
    : true
if (typeof window !== 'undefined') {
  window
    .matchMedia(LARGE_SCREEN_QUERY)
    .addEventListener('change', (e: MediaQueryListEvent) => {
      _isLargeScreen = e.matches
      _subscribers.forEach((cb) => cb(e.matches))
    })
}

function useSharedIsLargeScreen(): boolean {
  const [value, setValue] = useState(_isLargeScreen)
  useEffect(() => {
    setValue(_isLargeScreen)
    _subscribers.add(setValue)
    return () => {
      _subscribers.delete(setValue)
    }
  }, [])
  return value
}

/**
 * A compact stamp for the hover card — the time alone for today, date + time otherwise. The name beside
 * it truncates, so the timestamp never gets pushed out even on the longer form.
 */
function formatPeekTime(value: string): string {
  const when = new Date(value)
  if (Number.isNaN(when.getTime())) return ''

  const now = new Date()
  const sameDay =
    when.getFullYear() === now.getFullYear() &&
    when.getMonth() === now.getMonth() &&
    when.getDate() === now.getDate()

  return sameDay
    ? when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : when.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/**
 * The grid cell's message bubble, in the three renders the design specifies.
 *
 * <b>Hollow versus filled is the primary signal</b>, carrying "no conversation" against "a
 * conversation exists" — colour alone cannot do that job, because the empty and read states are both
 * quiet greys-to-blues and a row with nothing on it must be distinguishable at a glance from one you
 * have simply already read.
 *
 * The important state puts a white "!" INSIDE the bubble rather than beside the count: next to the
 * digits it misreads as part of a two-digit number. The bubble itself never turns red — red is left to
 * mean exactly one thing, the count — and importance gets a shape carrier as well as a colour one.
 *
 * Hand-drawn rather than lucide's MessageCircle because that icon has no filled variant, so every
 * state rendered as the same outline and only the hue moved.
 */
function MessageBubble({ filled, important, size }: { filled: boolean; important: boolean; size: string }) {
  const outline = "M21 12a8 8 0 0 1-11.4 7.2L4 20l1-4.2A8 8 0 1 1 21 12z"
  const shared = {
    className: 'entity-chat-trigger-icon',
    width: size,
    height: size,
    // The bubble is drawn on a 24-native grid — its tail reaches y≈20 and the arc x≈21 — so a tight
    // `0 0 20 20` viewBox clipped the stroked tail at the bottom-right. It read as a flattened bubble
    // bottom in the grid, where the cell clips overflow so the tail cannot spill. `1 1 22 22` stays
    // zoomed in close (still filling the button) but leaves room for the full stroked path, tail
    // included, to render inside the icon's own box.
    viewBox: '1 1 22 22',
    // Stroke on EVERY state, not just the hollow one. A fill-only bubble draws to the path edge while a
    // stroked one extends ~1px past it, so empty (hollow) bubbles rendered visibly larger than filled
    // ones — the inconsistency seen in every notification state. Same stroke everywhere → identical outer
    // size across all four states; only the fill differs (none vs currentColor).
    stroke: 'currentColor',
    strokeWidth: 1.85,
    'aria-hidden': true as const
  }

  if (important) {
    return (
      <svg {...shared} fill="currentColor">
        <path d={outline} />
        <path d="M13 8.1v4.6M13 15.9h.01" stroke="#fff" strokeWidth="2.1" strokeLinecap="round" />
      </svg>
    )
  }

  return (
    <svg {...shared} fill={filled ? 'currentColor' : 'none'}>
      <path d={outline} />
    </svg>
  )
}

export default function EntityChatTrigger({
  entityTypeId,
  entityId,
  variant = 'icon',
  size = 'md',
  position = 'bottom-right', // For floating variant
  panelPosition = 'right',
  slideOrigin = 'right',
  animationDuration = 300,
  className,
  sx,
  language = 'en',
  customTranslations,
  title,
  internalOnly = false,
  showRelatedEntities = true,
  autoIncludeRelatedTypes = [],
  markReadOnOpen = true,
  summaryMode = 'off',
}: EntityChatTriggerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [messageCount, setMessageCount] = useState(0)
  const [mounted, setMounted] = useState(false)
  const isLargeScreen = useSharedIsLargeScreen()
  const [isAnimating, setIsAnimating] = useState(false)
  const [shouldSlideIn, setShouldSlideIn] = useState(false)

  // Panel resizing state - only for large screens
  const [panelSize, setPanelSize] = useState(() => ({
    width: '40vw',
    height: '50vh',
  }))
  const [isResizing, setIsResizing] = useState(false)
  // Where the hover preview is pinned, in viewport coordinates, or null when hidden. The peek is
  // portaled to <body> at these coordinates rather than positioned inside the cell — see openPeek.
  const [peekAnchor, setPeekAnchor] = useState<{ top: number; left: number } | null>(null)
  const resizeStartRef = React.useRef<{
    startX: number
    startY: number
    startWidth: number
    startHeight: number
  } | null>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)

  const { config } = useMessageComponents()
  const { userToken, sx: providerSx } = config
  // Initialize i18n synchronously before any hooks to ensure consistent useTranslation behavior
  initializeI18n(language, customTranslations)
  const { t } = useTranslation()
  // Merge provider sx with component sx (component sx has higher priority)
  const mergedSx = useMemo(() => mergeSx(providerSx, sx), [providerSx, sx])

  // A genuine open (the panel was closed) lifts any deliberate-unread suppression on THIS thread, so
  // opening marks it read (classic behaviour). Two guards:
  //   - `!isOpen`: handleOpen also fires while the panel is ALREADY open — keyboard Enter/Space refiring
  //     the focused trigger button, or a floating-variant click in a corner the panel doesn't cover.
  //     There setIsOpen is a no-op, so clearing the suppression would drop the reader's deliberate unread
  //     WITHOUT the mark-read meant to replace it (the panel's on-open effect has already run and won't
  //     re-fire). Only clear on the close→open transition.
  //   - `markReadOnOpen`: when false this instance never sends a mark-read (the panel's on-open effect is
  //     gated on it too), so it has nothing to clear for.
  // Cleared synchronously, before setIsOpen mounts the panel, so it lands BEFORE mark-read-on-open runs.
  // The suppression Set is keyed by entity, not by instance: a genuine open is a global fact about the
  // reader, so this (like the panel's own mark-read toggle) lifts the entity's entry for every mounted
  // trigger — which is correct, not a cross-instance leak.
  const handleOpen = useCallback(() => {
    if (!isOpen && markReadOnOpen) allowMarkRead(entityTypeId, entityId)
    setIsOpen(true)
  }, [isOpen, entityTypeId, entityId, markReadOnOpen])

  // Legacy per-row count path, kept for summaryMode 'off'. WorkOrder still depends on it: for an
  // internal caller message-count merges app.Note offboarding tasks that are not Cosmos documents at
  // all, arithmetic the conversation aggregate cannot express.
  const legacyApiService = useMessageApi()

  // One batched summaries call for every trigger mounted in the same tick, instead of one
  // message-count call per row. Skipped entirely while summaryMode is 'off'.
  // 'none' fetches nothing (see the legacy-count effect below), so it must also skip the summaries path.
  const summaryEnabled = summaryMode !== 'off' && summaryMode !== 'none'
  const { summary, updateSummary, error: summaryError } = useConversationSummary(
    summaryEnabled ? entityTypeId : '',
    summaryEnabled ? entityId : ''
  )

  // Whether THIS entity has anything to clear. "Already read" means the summary reports no unread — NOT
  // that the reader is unsubscribed. An internal reader reaches a thread through the `internal` access
  // key with no subscription of their own, so gating on isSubscribed skipped mark-read-on-open for
  // them: the grid badge cleared optimistically on open and then came straight back because the read was
  // never sent. The server no-ops the read for a genuine non-participant anyway (it is fire-and-forget),
  // so the only cost of dropping that gate is one harmless no-op request in the rare case someone has
  // unread without a subscription.
  // Deliberately separate from markReadOnOpen: that prop is the notifications
  // preference and must keep applying to the related entities the panel can open, which this
  // trigger knows nothing about.
  // Only a LOADED summary can say "already read". While the batched summaries call is in flight (or if
  // it failed) summary is null, and a null must fall through to "we don't know" → let the server decide
  // on open (it no-ops for a non-participant anyway). Treating the loading window as "already read"
  // skipped mark-read-on-open for a row clicked before its summary landed.
  const entityAlreadyRead = summaryEnabled && summary !== null
    ? !((summary.unreadCount ?? 0) > 0 || summary.hasImportantUnread)
    : false

  // Honour the same mark-read suppression the panel does: a thread the reader deliberately marked unread
  // must not have its badge silently cleared on close (the panel sends no read for it, so the grid would
  // show read with no server write and a refresh would bring the unread back).
  const willMarkRead = markReadOnOpen && !entityAlreadyRead && !isMarkReadSuppressed(entityTypeId, entityId)
  // helper
  const slugifyClassName = (v: string) =>
    v
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')

  // Get positioning styles for floating variant
  const getPositionStyles = (): React.CSSProperties => {
    switch (position) {
      case 'bottom-left':
        return { bottom: '16px', left: '16px' }
      case 'top-right':
        return { top: '16px', right: '16px' }
      case 'top-left':
        return { top: '16px', left: '16px' }
      case 'bottom-right':
      default:
        return { bottom: '16px', right: '16px' }
    }
  }

  // Panel slide positioning logic
  const getSlideClasses = () => {
    const baseClasses = 'entity-chat-panel-base'

    switch (panelPosition) {
      case 'left':
        return `${baseClasses} entity-chat-panel-position-left`
      case 'right':
        return `${baseClasses} entity-chat-panel-position-right`
      case 'top':
        return `${baseClasses} entity-chat-panel-position-top`
      case 'bottom':
        return `${baseClasses} entity-chat-panel-position-bottom`
      default:
        return `${baseClasses} entity-chat-panel-position-right`
    }
  }

  const getSlideTransform = (
    shouldSlideIn: boolean,
    slideOrigin: 'left' | 'right' | 'bottom' | 'top',
    panelPosition: 'left' | 'right' | 'bottom' | 'top'
  ) => {
    if (shouldSlideIn) return 'translate(0, 0)'

    const transforms: Record<
      'left' | 'right' | 'bottom' | 'top',
      Partial<Record<'left' | 'right' | 'bottom' | 'top', string>> & {
        default: string
      }
    > = {
      left: {
        right: 'translateX(-100vw)',
        default: 'translateX(-100%)',
      },
      right: {
        left: 'translateX(100vw)',
        default: 'translateX(100%)',
      },
      bottom: {
        top: 'translateY(100vh)',
        default: 'translateY(100%)',
      },
      top: {
        bottom: 'translateY(-100vh)',
        default: 'translateY(-100%)',
      },
    }

    const originTransforms = transforms[slideOrigin]
    if (!originTransforms) return 'translate(0, 0)'

    return originTransforms[panelPosition] ?? originTransforms.default
  }

  // Function to handle message count updates from the panel
  const handleMessageCountChanged = (count: number): void => {
    setMessageCount(count)
    // A total, so it can only move the total. Unread is unaffected: the only thing that changes the
    // count from inside the panel is the reader sending, and a sender is never unread on their own
    // message.
    if (summaryEnabled) {
      updateSummary((current) => ({ ...current, messageCount: count }))
    }
  }

  // In 'unread' mode the badge is this reader's unread count and nothing else: a total would put a
  // number on every row and make the one that needs attention indistinguishable.
  // 'hidden' still FETCHES — the summary carries the preview snapshot the hover card renders, and
  // suppressing the request would take that away too. It only withholds the number.
  const badgeCount = summaryMode === 'hidden'
    ? 0
    : summaryEnabled
      ? summaryMode === 'unread'
        ? (summary?.unreadCount ?? 0)
        : (summary?.messageCount ?? 0)
      : messageCount

  /**
   * The four states the design specifies for the grid cell, as classes rather than inline colours.
   *
   * The distinction that matters is the middle one: a thread with messages you have already read still
   * has to look different from a row with no messages at all, or "nothing here" and "nothing new" are
   * indistinguishable and the icon stops being worth looking at. Important outranks unread, because a
   * flagged message is the reason to look now.
   */
  const hasAnyMessages = summaryEnabled
    ? (summary?.messageCount ?? 0) > 0
    : messageCount > 0
  // Notifications off ('hidden'): withhold EVERY urgency signal. The summary is still fetched for the
  // preview, but unread/important would otherwise still colour the icon and draw the "!", so a thread
  // that has messages must collapse to the single quiet "read" bubble — same shape and colour for all,
  // no exclamation, no badge. That is the whole promise of turning notifications off.
  const showUrgency = summaryMode !== 'hidden'
  const hasUnread = showUrgency && (summary?.unreadCount ?? 0) > 0
  const hasImportantUnread = showUrgency && summary?.hasImportantUnread === true

  // A failed summaries batch reads as UNKNOWN, not "no messages": a 403/500/timeout on one page must not
  // make every cell claim its thread is empty (and hide real messages). Only a definitive summary === null
  // (loaded, no error) draws the hollow empty state; on error render the neutral read bubble. The hook
  // keeps its last value on error, so a transient failure shows the last-known state.
  const summaryErrored = summaryEnabled && summaryError != null && summary === null
  const cellStateClass = summaryErrored
    ? 'entity-chat-trigger-state-read'
    : !hasAnyMessages
      ? 'entity-chat-trigger-state-empty'
      : hasUnread
        ? 'entity-chat-trigger-state-unread'
        : 'entity-chat-trigger-state-read'

  /**
   * The hover card: who wrote the newest message and what it said.
   *
   * Rendered from the summary the row already fetched, so hovering costs nothing. This is why
   * `summaryMode: 'hidden'` still fetches — it withholds the number, not the preview, and suppressing
   * the request would take the card away too.
   *
   * `pointer-events: none` in CSS on purpose: the card overlaps neighbouring rows, and a hover target
   * that swallows clicks meant for the row underneath is worse than no card at all.
   */
  const PEEK_WIDTH = 260

  /**
   * Pin the preview to the hovered icon, in viewport coordinates.
   *
   * The grid's scroll container is `overflow: auto`, so a preview positioned inside the cell is clipped
   * by it and trapped in the cell's stacking context — which is exactly the "preview appears behind the
   * grid" report. A portal to <body> with `position: fixed` escapes both the clip and the stacking
   * context. The trade is that it no longer moves with the row on scroll, so it closes on mouse-out,
   * which is the normal behaviour of a hover card anyway.
   */
  const openPeek = (event: React.MouseEvent) => {
    if (!summaryEnabled || !hasAnyMessages || typeof window === 'undefined') return
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
    // Open to the right of the icon; flip left only if the right edge would run off-screen. The message
    // column is pinned far left, so the flip is a safety net rather than the usual case.
    const opensRight = rect.right + 8 + PEEK_WIDTH <= window.innerWidth
    const left = opensRight ? rect.right + 8 : Math.max(8, rect.left - 8 - PEEK_WIDTH)
    // Keep it on screen vertically without chasing the cursor — clamp the top rather than letting a
    // bottom-row preview run off the viewport.
    const top = Math.min(Math.max(8, rect.top - 4), window.innerHeight - 150)
    setPeekAnchor({ top, left })
  }

  const closePeek = () => setPeekAnchor(null)

  const renderPeek = () => {
    if (!peekAnchor || !summaryEnabled || !hasAnyMessages || typeof document === 'undefined') return null

    const preview = summary?.preview
    const authorName = preview?.authorName || t('messages.unknownAuthor')

    return createPortal(
      <span
        className={cn(
          'entity-chat-trigger-peek entity-chat-trigger-peek-portal',
          hasImportantUnread
            ? 'entity-chat-trigger-peek-important'
            : hasUnread && 'entity-chat-trigger-peek-unread'
        )}
        style={{ top: peekAnchor.top, left: peekAnchor.left, width: PEEK_WIDTH }}
        aria-hidden="true"
      >
        {/* Per the design: a single header line of [status dot][name][time] — no avatar. The dot is an
            inline element on the line (blue unread / red important), NOT attached to anything. */}
        <span className="entity-chat-trigger-peek-head">
          {hasUnread && <i className="entity-chat-trigger-peek-dot" />}
          {/* No snapshot but there ARE messages (a thread adopted by CreateAsync seeds counters, not the
              preview snapshot): show the count in the name slot so the hover still has something to say. */}
          <b>{preview ? authorName : t('messages.messageCount', { count: summary?.messageCount ?? 0 })}</b>
          {preview && (
            <span className="entity-chat-trigger-peek-time">{formatPeekTime(preview.createdAt)}</span>
          )}
        </span>
        {preview?.snippet && <span className="entity-chat-trigger-peek-body">{preview.snippet}</span>}
        {hasImportantUnread ? (
          <span className="entity-chat-trigger-peek-foot">
            {t('messages.markedImportantBy', { name: authorName })}
          </span>
        ) : hasUnread ? (
          <span className="entity-chat-trigger-peek-foot">
            {t('messages.unreadCount', { count: summary?.unreadCount ?? 0 })}
          </span>
        ) : null}
      </span>,
      document.body
    )
  }

  // Resize handlers for panel
  const handleResizeStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)

    const rect = panelRef.current?.getBoundingClientRect()
    if (!rect) return
    resizeStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
    }
  }, [])

  const handleResizeMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !resizeStartRef.current) return

      const { startX, startY, startWidth, startHeight } = resizeStartRef.current
      const deltaX = e.clientX - startX
      const deltaY = e.clientY - startY

      let newWidth = startWidth
      let newHeight = startHeight

      // Calculate new dimensions based on panel position
      if (panelPosition === 'right') {
        newWidth = Math.max(
          300,
          Math.min(window.innerWidth * 0.8, startWidth - deltaX)
        )
      } else if (panelPosition === 'left') {
        newWidth = Math.max(
          300,
          Math.min(window.innerWidth * 0.8, startWidth + deltaX)
        )
      } else if (panelPosition === 'top') {
        newHeight = Math.max(
          300,
          Math.min(window.innerHeight * 0.8, startHeight + deltaY)
        )
      } else if (panelPosition === 'bottom') {
        newHeight = Math.max(
          300,
          Math.min(window.innerHeight * 0.8, startHeight - deltaY)
        )
      }

      setPanelSize({
        width: `${newWidth}px`,
        height: `${newHeight}px`,
      })
    },
    [isResizing, panelPosition]
  )

  const handleResizeEnd = React.useCallback(() => {
    setIsResizing(false)
    resizeStartRef.current = null
  }, [])

  // Fetch initial message count when component mounts or entity changes
  // This provides a fallback count before the panel is opened
  useEffect(() => {
    // 'none' makes NO call at all (notifications off): skip the legacy count too, not just summaries.
    if (summaryEnabled || summaryMode === 'none') return

    const fetchCount = async () => {
      try {
        const count = await legacyApiService.fetchMessageCountWithToken(
          entityTypeId,
          entityId
        )
        setMessageCount(count)
      } catch (error) {
        // Error is already logged in the service
      }
    }

    if (userToken) {
      fetchCount()
    }
    // summaryMode is read above ('none' skips the call): summaryEnabled is false for BOTH 'off' and
    // 'none', so without summaryMode here a trigger going none → off (notifications turned back on)
    // would never re-run and the legacy count would never fire.
  }, [entityTypeId, entityId, userToken, legacyApiService, summaryEnabled, summaryMode])

  // Clear the badge locally when a panel we asked to mark read closes.
  //
  // Applied rather than re-fetched: mark-read has one exact effect on this row, so re-reading the
  // whole page's summaries to discover it is a round trip that buys nothing. Somebody else's message
  // still needs a real refresh — that is the refresh tick's job, not this one's.
  //
  // Centralised on the close edge rather than on each of the three ways to close the panel (button,
  // overlay click, Escape), which is how one of them ends up forgotten.
  const wasOpenRef = React.useRef(false)

  // What the panel last told us this thread is at, if the reader changed it by hand. Null means they
  // did not, and the close-edge clear below is correct. Anything else wins over it: an explicit
  // "mark unread" is a deliberate instruction to keep the badge, and clearing it on close would make
  // the feature impossible to use — you would mark a message unread and watch the count vanish.
  const explicitReadStateRef = React.useRef<{ unreadCount: number; hasImportantUnread: boolean } | null>(null)

  const handleUnreadChange = useCallback((unreadCount: number, hasImportantUnread: boolean) => {
    explicitReadStateRef.current = { unreadCount, hasImportantUnread }
    updateSummary((current) => ({ ...current, unreadCount, hasImportantUnread }))
  }, [updateSummary])

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      const explicit = explicitReadStateRef.current
      if (explicit) {
        updateSummary((current) => ({ ...current, ...explicit }))
      } else if (willMarkRead) {
        updateSummary((current) => ({ ...current, unreadCount: 0, hasImportantUnread: false }))
      }
      // Reset for the next open — the snapshot is only meaningful within one visit.
      explicitReadStateRef.current = null
    }
    wasOpenRef.current = isOpen
  }, [isOpen, willMarkRead, updateSummary])

  // Handle mounting for portal
  useEffect(() => {
    setMounted(true)
  }, [])

  // Reset panel size when screen size changes
  const prevIsLargeScreenRef = React.useRef(isLargeScreen)
  useEffect(() => {
    if (prevIsLargeScreenRef.current !== isLargeScreen) {
      setPanelSize({
        width: '40vw',
        height: '50vh',
      })
      prevIsLargeScreenRef.current = isLargeScreen
    }
  }, [isLargeScreen])

  // Handle animation state for smooth transitions
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true)
      // Small delay to ensure DOM is ready, then trigger slide-in
      const slideTimeout = setTimeout(() => {
        setShouldSlideIn(true)
      }, 10)
      return () => clearTimeout(slideTimeout)
    } else {
      // Start slide-out immediately
      setShouldSlideIn(false)
      // Keep animating for the duration of the transition
      const timeout = setTimeout(() => {
        setIsAnimating(false)
      }, animationDuration) // Match the CSS transition duration
      return () => clearTimeout(timeout)
    }
  }, [isOpen, animationDuration])

  // Lock body scroll when panel is open and add escape key handler
  useEffect(() => {
    if (!isOpen) return () => {}

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Add escape key listener
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  // Add global mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
      document.body.style.cursor =
        panelPosition === 'top' || panelPosition === 'bottom'
          ? 'ns-resize'
          : 'ew-resize'
      document.body.style.userSelect = 'none'

      return () => {
        document.removeEventListener('mousemove', handleResizeMove)
        document.removeEventListener('mouseup', handleResizeEnd)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
  }, [isResizing, handleResizeMove, handleResizeEnd, panelPosition])

  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  }

  const getIconSize = (size: string): string => {
    const sizeMap = {
      sm: '12px',
      md: '30px',
      lg: '32px',
    }
    return sizeMap[size as keyof typeof sizeMap] || sizeMap.md
  }

  // Show loading state or render with available data
  // Component can render before user is loaded to show initial count

  // Generate stable class for this instance
  const instanceClass = useMemo(
    () => `entity-chat-trigger-${slugifyClassName(String(entityTypeId))}-${slugifyClassName(String(entityId))}`,
    [entityTypeId, entityId]
  )

  // Panel overlay component - only render when mounted and panel should be shown
  const renderPanelOverlay = () => {
    if (!mounted || (!isOpen && !isAnimating)) return null

    return (
      <div className="entity-chat-panel-container">
        {/* Overlay - fades in/out */}
        <div
          className="entity-chat-panel-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: shouldSlideIn
              ? 'rgba(0, 0, 0, 0.5)'
              : 'rgba(0, 0, 0, 0)',
            zIndex: 999,
            pointerEvents: isAnimating ? 'auto' : 'none',
            transition: `background-color ${animationDuration}ms ease-in-out`,
          }}
          role="button"
          tabIndex={0}
          aria-label={t('accessibility.closeChatPanel')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsOpen(false)
            }
          }}
          onClick={() => setIsOpen(false)}
          onWheel={(e) => e.preventDefault()}
          onTouchMove={(e) => e.preventDefault()}
        />

        {/* Sliding Chat Panel Container */}
        <div
          ref={panelRef}
          className={cn(
            getSlideClasses(),
            'entity-chat-panel-wrapper',
            instanceClass
          )}
          style={{
            position: 'fixed',
            ...(panelPosition === 'top' || panelPosition === 'bottom'
              ? {
                  height: isLargeScreen ? panelSize.height : '50vh',
                  minHeight: '300px',
                  maxHeight: '80vh',
                  width: '100vw',
                }
              : {
                  height: 'calc(100vh - var(--entity-chat-top-offset, 0px))',
                  width: isLargeScreen ? panelSize.width : '100vw',
                  minWidth: isLargeScreen ? '300px' : '100vw',
                  maxWidth: isLargeScreen ? '80vw' : '100vw',
                }),
            zIndex: 1000,
            transform: getSlideTransform(
              shouldSlideIn,
              slideOrigin,
              panelPosition
            ),
            transition: `transform ${animationDuration}ms ease-in-out`,
          }}
        >
          <EntityChatPanel
            entityTypeId={entityTypeId}
            entityId={entityId}
            onClose={() => setIsOpen(false)}
            className={instanceClass}
            language={language}
            customTranslations={customTranslations}
            // Withheld in 'unread' mode on purpose: reporting the total back is what makes the panel
            // fire its own message-count call after every load, and that number is never rendered
            // here. Passing nothing skips the request entirely.
            onMessageCountChanged={summaryMode === 'unread' ? undefined : handleMessageCountChanged}
            title={title}
            showCloseButton={true}
            internalOnly={internalOnly}
            showRelatedEntities={showRelatedEntities}
            autoIncludeRelatedTypes={autoIncludeRelatedTypes}
            markReadOnOpen={markReadOnOpen}
            entityAlreadyRead={entityAlreadyRead}
            initialUnreadCount={summary?.unreadCount}
            initialIsSubscribed={summary?.isSubscribed}
            onUnreadChange={handleUnreadChange}
          />

          {/* Resize Handle */}
          {isLargeScreen && (
            <div
              className={cn(
                'entity-chat-panel-resize-handle',
                panelPosition === 'right' &&
                  'entity-chat-panel-resize-handle-left',
                panelPosition === 'left' &&
                  'entity-chat-panel-resize-handle-right',
                panelPosition === 'top' &&
                  'entity-chat-panel-resize-handle-bottom',
                panelPosition === 'bottom' &&
                  'entity-chat-panel-resize-handle-top'
              )}
              onMouseDown={handleResizeStart}
            />
          )}
        </div>
      </div>
    )
  }

  if (variant === 'floating') {
    return (
      <div className={cn('entity-chat-container', instanceClass)}>
        {/* Generate CSS for nested selectors - covers both trigger and panel */}
        {mergedSx && <style>{sxToStyleTag(mergedSx, instanceClass)}</style>}
        {/* Chat Toggle Button */}
        <div
          className="entity-chat-floating-button-container"
          style={getPositionStyles()}
        >
          <Button
            onClick={handleOpen}
            className="entity-chat-floating-button"
            variant="ghost"
            size="icon"
            aria-label={t('accessibility.openChat')}
          >
            <MessageCircle className="entity-chat-floating-icon" />
            {badgeCount > 0 && (
              <Badge
                variant="destructive"
                className="entity-chat-floating-badge"
              >
                {badgeCount > 9 ? '9+' : badgeCount}
              </Badge>
            )}
          </Button>
        </div>

        {/* Panel Overlay - rendered inline */}
        {renderPanelOverlay()}
      </div>
    )
  }

  if (variant === 'button') {
    return (
      <div
        className={cn(
          'entity-chat-trigger-container entity-chat-trigger-button-container',
          instanceClass
        )}
        style={sxToCSS(mergedSx)}
      >
        {/* Generate CSS for nested selectors - covers both trigger and panel */}
        {mergedSx && <style>{sxToStyleTag(mergedSx, instanceClass)}</style>}
        <Button
          onClick={handleOpen}
          onMouseEnter={openPeek}
          onMouseLeave={closePeek}
          variant="outline"
          size="sm"
          className={cn('entity-chat-trigger-button', cellStateClass, className)}
          aria-label={t('accessibility.openChat')}
        >
          <MessageBubble
            filled={hasAnyMessages}
            important={hasImportantUnread}
            size={getIconSize(size)}
          />
          {badgeCount > 0 && (
            <Badge
              variant="secondary"
              className={cn(
                'entity-chat-trigger-badge entity-chat-trigger-badge-button',
                hasImportantUnread && 'entity-chat-trigger-badge-important'
              )}
            >
              {badgeCount > 99 ? '99+' : badgeCount}
            </Badge>
          )}
          {renderPeek()}
        </Button>

        {/* Panel Overlay - rendered inline */}
        {renderPanelOverlay()}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'entity-chat-trigger-container entity-chat-trigger-icon-container',
        instanceClass
      )}
      style={sxToCSS(mergedSx)}
    >
      {/* Generate CSS for nested selectors - covers both trigger and panel */}
      {mergedSx && <style>{sxToStyleTag(mergedSx, instanceClass)}</style>}
      {/* The Workflow Hub grid renders THIS variant, not the button one above. The four states and
          the hover preview were originally built only on that one, which is why the grid never showed
          them however the row was configured. Both variants carry them now. */}
      <Button
        onClick={handleOpen}
        onMouseEnter={openPeek}
        onMouseLeave={closePeek}
        variant="ghost"
        size="icon"
        title={hasAnyMessages || summaryErrored ? undefined : t('messages.noMessages')}
        className={cn(
          'entity-chat-trigger-icon-button',
          cellStateClass,
          sizeClasses[size as keyof typeof sizeClasses],
          className
        )}
        aria-label={t('accessibility.openChat')}
      >
        <MessageBubble
          filled={hasAnyMessages}
          important={hasImportantUnread}
          size={getIconSize(size)}
        />
        {badgeCount > 0 && (
          <Badge
            variant="secondary"
            className={cn(
              'entity-chat-trigger-badge entity-chat-trigger-badge-icon',
              hasImportantUnread && 'entity-chat-trigger-badge-important'
            )}
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </Badge>
        )}
        {renderPeek()}
      </Button>

      {/* Panel Overlay - rendered inline */}
      {renderPanelOverlay()}
    </div>
  )
}
